import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { PanelResizeHandle } from "react-resizable-panels";
import { css, Divider, styled, Typography, useTheme } from "@mui/material";
import { merge } from "lodash";
import { Variant } from "../Variant";
import { ResizeHandleBar } from "./ResizeHandleBar";

const hPath =
  "M18,16V13H15V22H13V2H15V11H18V8L22,12L18,16M2,12L6,16V13H9V22H11V2H9V11H6V8L2,12Z";
const vPath =
  "M8,18H11V15H2V13H22V15H13V18H16L12,22L8,18M12,2L8,6H11V9H2V11H22V9H13V6H16L12,2Z";

const StyledResizeHandle = styled(PanelResizeHandle, {
  shouldForwardProp: (prop) =>
    prop !== "$size" && prop !== "$orientation" && prop !== "$backgroundColor",
})<{
  $size: string;
  $orientation: "vertical" | "horizontal";
  $backgroundColor: string;
}>(
  ({ $size, $orientation, $backgroundColor }) => css`
    flex: 0 0 ${$size};
    position: relative;
    outline: none;
    background-color: ${$backgroundColor};
    display: flex;
    ${$orientation === "vertical" &&
    `
      max-width: ${$size};
    `}
    ${$orientation === "horizontal" &&
    `
      max-height: ${$size};
    `}
  `
);

export interface ResizeHandleProps {
  type: "regular" | "mobile";
  variant: Variant;
  thin?: boolean;
  collapsed?: boolean;
  label?: string;
  orientation: "horizontal" | "vertical";
  disableDivider?: boolean;
  hideHandle?: boolean;
  hideLabel?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
  onOpen?: () => void;
}

export const ResizeHandle: FC<ResizeHandleProps> = (props) => {
  const { orientation, variant, type } = props;
  const disabled = props.disabled ?? false;
  const disableDivider =
    disabled ||
    (props.disableDivider ?? (variant === "flat" || variant === "elevation"));
  const collapsed = props.collapsed ?? false;
  const hideLabel = props.hideLabel != null ? props.hideLabel : !collapsed;
  const hideHandle =
    disabled || !hideLabel || (props.hideHandle ?? variant === "flat");

  const thin = disabled || (props.thin ?? false);
  const theme = useTheme();
  const [hovering, setHovering] = useState(false);
  const [clicked, setClicked] = useState(false);

  const size = thin ? "2px" : "24px";
  const backgroundColorDefault = theme.palette.background.default;
  const backgroundColorWhenHovering = theme.palette.action.hover;
  const backgroundColorWhenClicked = theme.palette.action.hover;
  const backgroundColor = clicked
    ? backgroundColorWhenClicked
    : hovering
    ? backgroundColorWhenHovering
    : backgroundColorDefault;

  useEffect(() => {
    setHovering(false);
    setClicked(false);
  }, [collapsed]);

  const handleClick = () => {
    if (collapsed) {
      props.onOpen?.();
    }
  };

  const handleMouseEnter = () => {
    if (!disabled) {
      setHovering(true);
    }
  };
  const handleMouseLeave = () => {
    setHovering(false);
  };

  const handleMouseDown = () => {
    if (!disabled) {
      setClicked(true);
    }
  };

  const handleMouseUp = () => {
    setClicked(false);
  };

  const rootStyle = useMemo(() => {
    return merge({ margin: 0 }, props.style, {
      position: "relative",

      ...(props.type === "mobile" && {
        borderTop: "1px solid " + theme.palette.divider,
        borderLeft: "1px solid " + theme.palette.divider,
        borderRight: "1px solid " + theme.palette.divider,
        borderTopLeftRadius: "9px",
        borderTopRightRadius: "9px",
        alignItems: "center",
        justifyContent: "center",
      }),
    } as CSSProperties);
  }, [props.style, props.type, theme.palette.divider]);

  return (
    <StyledResizeHandle
      $size={size}
      $orientation={orientation}
      $backgroundColor={backgroundColor}
      style={rootStyle}
      disabled={disabled}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {type === "mobile" && <ResizeHandleBar />}
      {props.label != null && !hideLabel && (
        <Typography
          component={"div"}
          variant={"button"}
          color={"secondary"}
          align={orientation === "horizontal" ? "center" : undefined}
          sx={{
            ...(orientation === "horizontal" && {
              paddingLeft: 1,
              paddingRight: 1,
            }),
            ...(orientation === "vertical" && {
              position: "absolute",
              top: "50%",
              bottom: "50%",
              transform: "translateX(-50%) translateY(-50%) rotate(-90deg)",
              minWidth: "unset",
              whiteSpace: "nowrap",
            }),
          }}
        >
          {props.label}
        </Typography>
      )}
      {type === "mobile" && props.label != null && !hideLabel && (
        <ResizeHandleBar />
      )}
      {!disableDivider && (
        <Divider orientation={orientation}>
          {!hideHandle && (
            <svg viewBox="0 0 24 24">
              <path
                fill={theme.palette.divider}
                d={orientation === "horizontal" ? vPath : hPath}
              />
            </svg>
          )}
        </Divider>
      )}
      {disableDivider && !hideHandle && (
        <svg viewBox="0 0 24 24">
          <path
            fill={theme.palette.divider}
            d={orientation === "horizontal" ? vPath : hPath}
          />
        </svg>
      )}
    </StyledResizeHandle>
  );
};
